import ApplicationResponsiveModal from '../common/ApplicationResponsiveModal'

export const PrivacyModal = ({show, onClose}: {show: boolean; onClose: () => void}) => {
  if (!show) return null

  return (
    <ApplicationResponsiveModal
      ariaLabelledBy='application-privacy-title'
      closeAriaLabel='Close privacy policy'
      onClose={onClose}
      modalClassName='application-legal-modal'
      closeButtonClassName='application-legal-modal-close'
      hideCloseButtonOnMobile
      mobileAnimationMs={380}
      swipeTarget='handle'
    >
      <div className='application-legal-modal-body'>
        <div className='application-legal-modal-content terms'>
          <h2 id='application-privacy-title' className='title'>
            DOORA
          </h2>
          <h3 className='subtitle'>Privacy Policy</h3>
          <hr></hr>
          <p className='caption'>
            RL Group Australia Pty Ltd (ACN 690 919 307)
            <br />
            trading as DOORA
            <br />
            <strong>Effective Date: 1 July 2026</strong>
          </p>
        <h4>1. About This Policy</h4>
        <p>
          This Privacy Policy explains how RL Group Australia Pty Ltd (ACN 690 919 307), trading as
          DOORA, collects, uses, stores, discloses, and deletes personal information. It applies to
          all users of the DOORA platform, including renters, co-applicants, and property managers.
        </p>
        <p>
          DOORA voluntarily complies with the Australian Privacy Principles (APPs 1–13) under the
          Privacy Act 1988 (Cth), regardless of whether the small business exemption applies.
        </p>
        <p>
          This Policy is the single canonical privacy document for the DOORA platform. It must be
          read together with the Renter Terms of Service and the Property Manager Terms of Service,
          which govern your use of the platform. If there is any inconsistency between this Policy
          and a privacy-related term in another DOORA document, this Policy prevails.
        </p>
        <h4>2. Who We Are</h4>

        <div className='table'>
          <div className='row '>
            <div className='rowTitle'>Entity</div>
            <div>RL Group Australia Pty Ltd (ACN 690 919 307) </div>
          </div>

          <div className='row'>
            <div className='rowTitle'>Trading name</div>
            <div>DOORA</div>
          </div>

          <div className='row'>
            <div className='rowTitle'>Privacy Officer</div>
            <div>Christopher Haridemos (Founder and Sole Director)</div>
          </div>

          <div className='row'>
            <div className='rowTitle'>Contact</div>
            <div>privacy@doora.com.au</div>
          </div>

          <div className='row'>
            <div className='rowTitle'>Address</div>
            <div>46 St Pauls St, Randwick NSW 2031</div>
          </div>
        </div>
        <p></p>
        <h4>3. What DOORA Does</h4>
        <p>
          DOORA is a renter preparation and navigation platform. Renters use DOORA to build a Doora
          Card — a standardised, portable rental application — and submit it to property managers of
          their choosing. The service is free to renters. DOORA generates revenue through property
          manager subscriptions and service integrations.
        </p>
        <p>
          DOORA Helpers — trained virtual assistants provided by a contracted outsourcing partner —
          guide renters through the preparation process via WhatsApp, Instagram, and Facebook
          Messenger. All Helper conversations are managed through our customer support ticketing
          platform.
        </p>
        <h4>4. What Personal Information We Collect</h4>
        <p>
          DOORA collects two distinct categories of personal information, each with its own data
          flow and access model.
        </p>
        <h5>4.1 Doora Card Data (entered by the renter)</h5>
        <p>
          Renters enter the following information directly into the DOORA platform when building
          their Doora Card. Helpers never enter this data on behalf of renters.
        </p>
        <div className='table'>
          <div className='row '>
            <div className='rowTitle'>Data type </div>
            <div className='rowTitle'>Examples </div>
            <div className='rowTitle'>Sensitivity </div>
          </div>

          <div className='row'>
            <div>Identity</div>
            <div>Full name, date of birth, phone number, email address </div>
            <div>Medium-High</div>
          </div>

          <div className='row'>
            <div>Government ID</div>
            <div>Driver licence or passport (type, number, scanned images)</div>
            <div>Very High</div>
          </div>
          <div className='row'>
            <div>Income and financial</div>
            <div>Payslips, tax returns, bank statements, Centrelink or benefit statements</div>
            <div>Very High </div>
          </div>
          <div className='row'>
            <div>Employment</div>
            <div>Employer name, employment type, start date, employer contact</div>
            <div>High</div>
          </div>
          <div className='row'>
            <div>Rental history</div>
            <div>
              Previous addresses, duration, rent amount, landlord or PM contact, rental ledger
            </div>
            <div>High</div>
          </div>
          <div className='row'>
            <div>Household</div>
            <div>Co-applicant details, pets, vehicles, household type</div>
            <div>Medium</div>
          </div>
          <div className='row'>
            <div>Preferences</div>
            <div>Move-in date, rent budget, lease term</div>
            <div>Low</div>
          </div>
          <div className='row'>
            <div>Documents</div>
            <div>Uploaded ID scans, payslips, visa grant notices, certificates of enrolment</div>
            <div>Very High</div>
          </div>
        </div>
        <p>For Company–Trust applications, DOORA also collects ABN/ACN and Director ID.</p>
        <h5>4.2 Conversation Data (Helper interactions)</h5>
        <p>
          When renters contact DOORA via WhatsApp, Instagram, or Facebook Messenger, their messages
          are routed through our customer support ticketing platform. Renters may voluntarily share
          personal details during these conversations, including income, housing circumstances, and
          personal situations.
        </p>
        <p>Helpers access Conversation Data only. Helpers never access Doora Card Data.</p>
        <h5>4.3 Technical and platform data</h5>
        <p>
          DOORA collects limited technical data necessary to operate the platform, including IP
          addresses, device type, browser type, and platform interaction logs. This data is used for
          security, error monitoring, and service improvement. It is not used for marketing,
          profiling, or behavioural targeting.
        </p>
        <h4>5. Lawful Basis for Collection</h4>
        <p>
          DOORA collects personal information because it is reasonably necessary to provide the
          DOORA service (APP 3.2). Specifically:
        </p>
        <ul>
          <li>
            <strong>General personal information (APP 3.2):</strong> DOORA collects identity,
            employment, rental history, and household information because it is reasonably necessary
            to build a Doora Card and submit rental applications on the renter's behalf.
          </li>
          <li>
            <strong>Sensitive information (APP 3.3):</strong> Government identification documents
            and financial records are sensitive information. DOORA obtains the renter's consent
            before collecting sensitive information. Consent is collected through a specific
            in-platform prompt before document upload. You may withdraw consent at any time by
            deleting the relevant information from your Doora Card or by deleting your account.
          </li>
          <li>
            <strong>Government-related identifiers (APP 9):</strong> Where you provide a
            government-related identifier (such as a Centrelink reference number, Medicare number,
            or driver licence number), DOORA uses that identifier solely to support your rental
            application. We do not adopt it as our own identifier, use it for internal profiling, or
            disclose it for any purpose other than delivery of the service.
          </li>
        </ul>
        <h4>6. How We Collect Personal Information</h4>
        <ul>
          <li>
            <strong>Doora Card Data:</strong> entered directly by the renter into the platform on
            their own device.
          </li>
          <li>
            <strong>Co-applicant data:</strong> entered by the co-applicant after OTP verification.
            The primary applicant provides the co-applicant's name and phone number to send the
            invitation.
          </li>
          <li>
            <strong>Conversation Data:</strong> provided voluntarily by the renter during Helper
            conversations via social messaging channels.
          </li>
          <li>
            <strong>Technical data:</strong> collected automatically when you use the platform.
          </li>
        </ul>
        <p>
          DOORA does not collect personal information from third parties, data brokers, or public
          sources. We do not conduct credit checks or background checks on renters. DOORA does not
          check, query, or report to any tenancy database (such as TICA, the National Tenancy
          Database, or equivalents).
        </p>
        <h4>7. Why We Collect Personal Information</h4>
        <p>We collect personal information for the following purposes:</p>
        <ul>
          <li>To build, store, and manage the renter's Doora Card.</li>
          <li>To submit the Doora Card to property managers selected by the renter.</li>
          <li>To enable Helpers to guide renters through the rental preparation process.</li>
          <li>
            To verify identity via OTP (one-time password) for account creation and co-applicant
            invitation.
          </li>
          <li>
            To send service communications: OTP codes, co-applicant invitations, and draft
            application reminders (maximum one SMS and one email).
          </li>
          <li>To operate, maintain, and improve the platform.</li>
          <li>To comply with applicable laws.</li>
        </ul>
        <p>
          We do not use personal information for marketing, advertising, behavioural profiling,
          renter scoring, renter ranking, or sale to any third party.
        </p>
        <h4>8. What We Will Never Do With Your Information</h4>
        <p>
          <strong>
            The following undertakings apply unless DOORA provides at least 30 days’ notice of a
            change, accompanied by a clear explanation.
          </strong>
        </p>
        <ul>
          <li>
            We will never sell, trade, or license your personal information to any third party.
          </li>
          <li>
            We will never use your information for marketing, advertising, or commercial purposes
            beyond delivering the DOORA service.
          </li>
          <li>
            We will not build a long-term profile or behavioural history of you for marketing,
            advertising, or commercial purposes. We may provide you with your own application
            history and journey information within the platform for your benefit.
          </li>
          <li>
            We will not build a long-term profile or behavioural history of you for marketing,
            advertising, or commercial purposes. We may provide you with your own application
            history and journey information within the platform for your benefit.
          </li>
          <li>
            We will never transmit your personal information to third-party systems for credit-like
            analysis, scoring, or matching.
          </li>
          <li>
            We will never send you marketing communications, newsletters, or promotional content.
          </li>
        </ul>
        <h4>9. Who We Share Personal Information With</h4>
        <h5>9.1 Property managers</h5>
        <p>
          When a renter submits their Doora Card to a property, the property manager receives a
          tokenised email link to a read-only web view of the submission. The web view is a frozen
          snapshot of the Card at the time of submission. The PM can preview uploaded documents
          in-browser but cannot download them during the application phase. The tokenised link
          expires after 30 days or on revocation by any applicant, whichever occurs first. The PM
          does not need to create an account or log in.
        </p>
        <h5>9.2 Doora Helpers</h5>
        <p>
          Helpers, employed through our contracted outsourcing partner, access Social Media
          Conversation Data via our customer support ticketing platform to provide renter guidance.
          Helpers do not have access to the DOORA platform. Their access is limited to social media
          conversations managed through the ticketing platform. Helpers cannot view, edit, or
          interact with any Doora Card. DOORA's use of this partner is governed by contractual
          obligations requiring the partner to handle personal information consistently with the
          Australian Privacy Principles.
        </p>
        <h5>9.3 Messaging platforms</h5>
        <p>
          Renter conversations are conducted via WhatsApp, Instagram, and Facebook Messenger,
          operated by Meta Platforms Inc and initiated by the renter. Messages pass through Meta's
          infrastructure. Meta's handling of messages on its own platforms is governed by Meta's
          privacy policies, not this Policy. DOORA's copy of these messages is held in our customer
          support ticketing platform and governed by this Policy.
        </p>
        <h5>9.4 Customer support ticketing platform</h5>
        <p>
          A US-based customer support ticketing platform manages Conversation Data as a service
          provider under its standard Data Processing Addendum.
        </p>
        <h5>9.5 Service providers</h5>
        <p>
          DOORA uses the following categories of third-party service provider to operate the
          platform:
        </p>
        <ul>
          <li>Our SMS delivery provider</li>
          <li>Our email delivery provider</li>
          <li>Our cloud infrastructure provider (Australian regions for Card Data)</li>
        </ul>
        <p>
          These providers process data only as necessary to deliver their services to DOORA. They do
          not use your personal information for their own purposes.
        </p>
        <h5>9.6 When required by law</h5>
        <p>
          DOORA may disclose personal information where required or authorised by Australian law,
          including in response to court orders, regulatory investigations, or to prevent serious
          threats to life, health, or safety.
        </p>
        <h4>10. Cross-Border Disclosure</h4>
        <p>
          Conversation Data is accessed by Helpers located in the Philippines via our customer
          support ticketing platform. The platform's infrastructure may route data through servers
          in the United States or other countries.
        </p>
        <p>
          Doora Card Data is stored on Australian-hosted infrastructure. Card Data does not leave
          Australia.
        </p>
        <p>
          Under APP 8.1 of the Privacy Act, DOORA takes reasonable steps to ensure overseas
          recipients handle personal information consistently with the APPs. These steps include
          contractual obligations on our outsourcing partner to handle personal information
          consistently with the APPs, and access controls limiting Helpers to Conversation Data
          only.
        </p>
        <p>
          Under section 16C of the Privacy Act, DOORA remains accountable for the handling of
          personal information by overseas recipients as if their acts were DOORA's own acts.
        </p>
        <p>
          DOORA's development and support partner may include staff located outside Australia;
          access to production data is governed by contractual privacy obligations consistent with
          the Australian Privacy Principles.
        </p>
        <h4>11. How Long We Keep Personal Information</h4>
        <h5>11.1 Doora Card Data</h5>
        <p>
          DOORA operates on a deletion-by-default model. Renters select their preferred retention
          period when creating their Doora Card. The selection can be changed at any time in account
          settings. The options are:
        </p>
        <ul>
          <li>
            <strong>Keep active</strong> — Card retained until the renter deletes it or confirms
            they have found a place.
          </li>
          <li>
            <strong>Auto-delete after 60 days</strong> — Card data automatically deleted 60 days
            after creation.
          </li>
          <li>
            <strong>Auto-delete after 90 days</strong> — Card data automatically deleted 90 days
            after creation.
          </li>
        </ul>
        <p>
          All identifiable Card data is deleted when the renter confirms they have found a place,
          requests deletion, or reaches the end of their selected retention period (60 or 90 days,
          as applicable). Renters may request immediate deletion at any time. Deletion is completed
          within 30 days of the request.
        </p>
        <h5>11.2 Property manager access</h5>
        <p>
          PM web view links expire 30 days after submission or immediately on revocation by any
          applicant. After expiry, the PM can no longer access the submission.
        </p>
        <h5>11.3 Conversation Data</h5>
        <p>
          Identifiable information in support tickets is purged when the ticket is closed. Closed
          tickets are deleted on a rolling schedule (maximum 30 days after closure). Note:
          conversations conducted via WhatsApp, Instagram, or Facebook Messenger are retained by
          Meta on its own platforms in accordance with Meta’s data policies. DOORA does not control
          Meta’s retention of those messages. DOORA’s deletion obligations under this section apply
          only to the copies held on our customer support ticketing platform.
        </p>
        <h5>11.4 Contact details</h5>
        <p>
          Phone number and email address are deleted on journey end, account deletion, or on-demand
          request. They are used only for OTP verification, co-applicant invitations, and draft
          reminders (maximum one SMS and one email). They are not used for marketing.
        </p>
        <h4>12. How We Protect Personal Information</h4>
        <p>
          DOORA implements security measures appropriate to the sensitivity of the personal
          information it holds. These measures include:
        </p>
        <ul>
          <li>Encryption of personal information in transit (TLS 1.2 or higher).</li>
          <li>Encryption of personal information at rest on platform servers.</li>
          <li>
            Role-based access controls restricting each user type to the data they need for their
            function.
          </li>
          <li>Authentication controls for administrative and Helper access.</li>
          <li>Platform access and audit logging.</li>
          <li>Deletion-by-default protocols to minimise the duration of data exposure.</li>
          <li>
            PM web view restricted to read-only with no document download capability during the
            application phase.
          </li>
          <li>
            Helpers restricted to Conversation Data only with no access to the DOORA platform or
            Doora Card Data.
          </li>
        </ul>
        <p>
          DOORA reviews and updates its security controls as the platform develops. Specific
          security measures are documented internally and are available for review by DOORA's legal
          and compliance advisers.
        </p>
        <p>
          If we suspect a data breach is likely to result in serious harm, we will assess it as soon
          as practicable and in any event within 30 days. If the assessment confirms an eligible
          data breach, we will notify affected individuals and the Office of the Australian
          Information Commissioner (OAIC) as soon as practicable, as required by the Notifiable Data
          Breaches scheme. We will tell you what happened, what information was involved, and what
          we are doing about it.
        </p>
        <h4>13. Artificial Intelligence</h4>
        <p>
          DOORA does not use artificial intelligence to read, assess, score, rank, or make decisions
          about renter applications or personal information. All decisions about rental applications
          are made by property managers, not by DOORA or any automated system.
        </p>
        <p>
          AI tools may be used internally by the DOORA team for engineering and operational purposes
          (such as code development). These tools do not process renter personal information.
        </p>
        <h4>14. Your Rights</h4>
        <p>Under the Australian Privacy Principles, you have the following rights:</p>
        <p>
          <strong>Anonymity.</strong> Where it is practicable, you can interact with DOORA
          pseudonymously. For Doora Card services, identification is required because property
          managers need to verify identity for tenancy applications.
        </p>
        <p>
          <strong>Access.</strong> You may request access to the personal information DOORA holds
          about you. Renters can view and edit their Doora Card directly on the platform at any
          time. For other access requests, contact privacy@doora.com.au.
        </p>
        <p>
          <strong>Correction.</strong> You may request correction of personal information that is
          inaccurate, out of date, incomplete, or misleading. Renters can edit their Card directly.
          For Conversation Data corrections, contact privacy@doora.com.au.
        </p>
        <p>
          <strong>Deletion.</strong> You may request deletion of your personal information at any
          time. Deletion is completed within 30 days. Any applicant in a shared household may revoke
          any property submission immediately.
        </p>
        <p>
          <strong>Revocation.</strong> You may revoke any property submission at any time.
          Revocation is immediate — the PM's access link is disabled and they can no longer view the
          submission.
        </p>
        <p>
          <strong>Co-applicants.</strong> Individual co-applicants may contact DOORA directly to
          request access to, correction of, or deletion of their own personal information,
          independently of the primary applicant.
        </p>
        <h4>15. Consent for Sensitive Documents</h4>
        <p>
          Before you upload any government-issued identity document (including your passport, driver
          licence, or proof of age card), tax return, payslip, Centrelink or government benefit
          statement, or bank statement, the platform will ask you to confirm your specific consent
          to:
        </p>
        <ul>
          <li>DOORA collecting and securely storing that document;</li>
          <li>
            sharing it with property managers you nominate when you submit your Doora Card to a
            specific property; and
          </li>
          <li>
            our overseas-based Helper staff being able to see message-based information you share
            with them (see Section 10).
          </li>
        </ul>
        <p>
          You can withdraw your consent at any time by deleting your Doora Card or the relevant
          document.
        </p>
        <h4>16. Co-Applicant Data</h4>
        <p>
          When a primary applicant invites a co-applicant, the co-applicant receives an SMS or email
          containing: the name of the person who shared their contact details, what DOORA is, and a
          link to this Privacy Policy. This is the co-applicant's first notification under APP 5.
        </p>
        <p>
          Co-applicants verify their identity via OTP and enter their own section of the Doora Card
          directly. The primary applicant provides the co-applicant's name and phone number for the
          invitation only. Applicants within a shared household cannot view each other's detailed
          financial information or uploaded documents.
        </p>
        <p>
          By inviting a co-applicant, the primary applicant confirms they have that person's
          permission to share their name and phone number with DOORA for the purpose of this
          invitation.
        </p>
        <p>
          Co-applicants who first contact DOORA via social messaging (WhatsApp, Instagram, or
          Facebook Messenger) will receive a link to this Privacy Policy in the Helper's first
          response.
        </p>
        <h4>17. Communications</h4>
        <p>DOORA sends the following service communications only:</p>
        <ul>
          <li>OTP codes for account verification and co-applicant invitation.</li>
          <li>
            Draft application reminders: maximum one SMS (24 hours after last activity) and one
            email (72 hours after last activity). No further follow-up.
          </li>
          <li>
            Agent share email: delivers the tokenised web view link to the property manager on
            submission.
          </li>
        </ul>
        <p>
          DOORA does not send marketing emails, newsletters, promotional content, or any
          communication beyond the service communications listed above.
        </p>
        <h4>18. Cookies and Platform Analytics</h4>
        <p>
          The DOORA platform may use cookies and analytics tools to understand how the platform is
          being used and to improve the experience. These tools collect aggregate, non-personal
          usage data (such as which features are being used, page load times, and error rates). No
          personal information is shared with analytics providers.
        </p>
        <p>
          Cookies used by the DOORA platform are limited to those necessary for platform
          functionality (such as maintaining your login session). DOORA does not use advertising
          cookies, tracking pixels, or third-party marketing cookies.
        </p>
        <h4>19. Changes to This Policy</h4>
        <p>
          DOORA may update this Privacy Policy from time to time. Material changes will be notified
          with at least 30 days' notice via the platform and, where possible, via your contact
          details. The updated Policy will be published on the DOORA website and within the
          platform.
        </p>
        <p>
          If you do not agree with a material change, you may delete your account and all data
          before the change takes effect. DOORA will not apply changed terms retroactively. Your
          continued use of the platform after the effective date of a change constitutes acceptance
          of the updated Policy.
        </p>
        <h4>20. Complaints</h4>
        <p>If you believe DOORA has breached the Australian Privacy Principles, you may:</p>
        <ul>
          <li>
            <strong>Contact DOORA's Privacy Officer</strong> at privacy@doora.com.au. DOORA will
            acknowledge your complaint within 5 business days and respond within 30 days.
          </li>
          <li>
            <strong>Lodge a complaint with the OAIC.</strong> If you are not satisfied with DOORA's
            response, you may lodge a complaint with the Office of the Australian Information
            Commissioner (OAIC) at www.oaic.gov.au or by calling 1300 363 992.
          </li>
        </ul>
        <h4>21. Contact</h4>
        <div className='table'>
          <div className='row '>
            <div className='rowTitle'>Privacy Officer</div>
            <div>Christopher Haridemos</div>
          </div>

          <div className='row'>
            <div className='rowTitle'>Email</div>
            <div>privacy@doora.com.au</div>
          </div>

          <div className='row'>
            <div className='rowTitle'>Address</div>
            <div>46 St Pauls St, Randwick NSW 2031</div>
          </div>

          <div className='row'>
            <div className='rowTitle'>OAIC</div>
            <div>www.oaic.gov.au | 1300 363 992</div>
          </div>
        </div>
        <p></p>
        <hr></hr>
        <p className='text-center'>RL Group Australia Pty Ltd (ACN 690 919 307)</p>
        <p></p>
        <p></p>
        <p></p>
          <button
            type='button'
            className='btn fw-bold py-2 px-3 rounded-pill btn-doora'
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </ApplicationResponsiveModal>
  )
}
